import React, { useState } from 'react';
import { getShareMessages, getMentorEmbedCode } from '../utils/referral';

// ── Copy helper ───────────────────────────────────────────────
function useCopy() {
  const [copied, setCopied] = useState('');
  const copy = (text, key) => {
    navigator.clipboard?.writeText(text).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(''), 2000);
    });
  };
  return { copied, copy };
}

// ── Social Button ─────────────────────────────────────────────
function SocialBtn({ icon, label, color, onClick }) {
  return (
    <button onClick={onClick} style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
      padding: '12px 16px', background: color + '18', border: `1.5px solid ${color}30`,
      borderRadius: 12, cursor: 'pointer', minWidth: 72, transition: 'all .2s',
      color,
    }}
    onMouseEnter={e => { e.currentTarget.style.background = color + '28'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
    onMouseLeave={e => { e.currentTarget.style.background = color + '18'; e.currentTarget.style.transform = 'translateY(0)'; }}
    >
      <span style={{ fontSize: 24 }}>{icon}</span>
      <span style={{ fontSize: 11, fontWeight: 600, color: '#374151' }}>{label}</span>
    </button>
  );
}

// ── Main Modal ────────────────────────────────────────────────
export default function ShareModal({ mentor, shareUrl, referralCode, onClose, mode = 'mentor' }) {
  const [tab, setTab] = useState('share'); // 'share' | 'embed' | 'referral'
  const [embedTheme, setEmbedTheme] = useState('light');
  const [embedSize, setEmbedSize] = useState('medium');
  const { copied, copy } = useCopy();

  const messages = getShareMessages(mentor, shareUrl);
  const embedCode = mentor ? getMentorEmbedCode(mentor, { theme: embedTheme, size: embedSize }) : '';

  const openUrl = (url) => window.open(url, '_blank', 'noopener');

  const handleWhatsApp = () => openUrl(`https://wa.me/?text=${encodeURIComponent(messages.whatsapp)}`);
  const handleTwitter  = () => openUrl(`https://twitter.com/intent/tweet?text=${encodeURIComponent(messages.twitter)}`);
  const handleLinkedIn = () => openUrl(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`);
  const handleFacebook = () => openUrl(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`);
  const handleTelegram = () => openUrl(`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(`🧠 Check out ${mentor?.name} on DevBrains!`)}`);
  const handleEmail    = () => openUrl(`mailto:?subject=${encodeURIComponent(messages.email.subject)}&body=${encodeURIComponent(messages.email.body)}`);

  const tabs = [
    { id: 'share',    label: '📤 Share',    show: true },
    { id: 'embed',    label: '🖥️ Embed',    show: mode === 'mentor' },
    { id: 'referral', label: '🎁 Referral', show: !!referralCode },
  ].filter(t => t.show);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: '#fff', borderRadius: 20, width: '100%', maxWidth: 520,
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)', overflow: 'hidden',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      }}>
        {/* Header */}
        <div style={{ background: '#1a1a2e', padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 18 }}>
              {mode === 'mentor' ? '📤 Share Your Profile' : '📤 Share & Refer'}
            </div>
            <div style={{ color: '#9ca3af', fontSize: 13, marginTop: 2 }}>
              {mode === 'mentor' ? 'Grow your audience & get more bookings' : 'Invite friends & earn credits'}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', width: 32, height: 32, borderRadius: '50%', cursor: 'pointer', fontSize: 16 }}>✕</button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid #f3f4f6', padding: '0 24px' }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              padding: '14px 16px', background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 13, fontWeight: 600,
              color: tab === t.id ? '#1DB954' : '#6b7280',
              borderBottom: tab === t.id ? '2px solid #1DB954' : '2px solid transparent',
              transition: 'all .2s',
            }}>{t.label}</button>
          ))}
        </div>

        <div style={{ padding: 24, maxHeight: '60vh', overflowY: 'auto' }}>

          {/* ── SHARE TAB ─────────────────────────────────── */}
          {tab === 'share' && (
            <>
              {/* Copy link */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>🔗 Share Link</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input readOnly value={shareUrl} style={{
                    flex: 1, padding: '10px 12px', borderRadius: 8, border: '1.5px solid #e5e7eb',
                    fontSize: 13, color: '#374151', background: '#f9fafb', outline: 'none',
                  }} onClick={e => e.target.select()} />
                  <button onClick={() => copy(shareUrl, 'link')} style={{
                    padding: '10px 16px', background: copied === 'link' ? '#1DB954' : '#1a1a2e',
                    color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer',
                    fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', transition: 'background .2s',
                  }}>
                    {copied === 'link' ? '✅ Copied!' : '📋 Copy'}
                  </button>
                </div>
              </div>

              {/* Social buttons */}
              <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 12 }}>📲 Share on Social</div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20 }}>
                <SocialBtn icon="💬" label="WhatsApp"  color="#25D366" onClick={handleWhatsApp} />
                <SocialBtn icon="🐦" label="Twitter"   color="#1DA1F2" onClick={handleTwitter} />
                <SocialBtn icon="💼" label="LinkedIn"  color="#0A66C2" onClick={handleLinkedIn} />
                <SocialBtn icon="📘" label="Facebook"  color="#1877F2" onClick={handleFacebook} />
                <SocialBtn icon="✈️" label="Telegram"  color="#26A5E4" onClick={handleTelegram} />
                <SocialBtn icon="📧" label="Email"     color="#6366f1" onClick={handleEmail} />
              </div>

              {/* Pre-written messages */}
              <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 12 }}>✍️ Ready-to-use Messages</div>
              {[
                { key: 'wa_msg',  label: '💬 WhatsApp message',  text: messages.whatsapp },
                { key: 'tw_msg',  label: '🐦 Twitter/X post',    text: messages.twitter },
                { key: 'li_msg',  label: '💼 LinkedIn post',      text: messages.linkedin },
              ].map(({ key, label, text }) => (
                <div key={key} style={{ marginBottom: 12, background: '#f9fafb', borderRadius: 10, padding: 12, border: '1px solid #e5e7eb' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#6b7280' }}>{label}</span>
                    <button onClick={() => copy(text, key)} style={{
                      padding: '4px 10px', background: copied === key ? '#1DB954' : '#1a1a2e',
                      color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 600,
                    }}>{copied === key ? '✅' : '📋 Copy'}</button>
                  </div>
                  <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.6, whiteSpace: 'pre-line', maxHeight: 80, overflow: 'hidden' }}>
                    {text.slice(0, 180)}{text.length > 180 ? '…' : ''}
                  </div>
                </div>
              ))}
            </>
          )}

          {/* ── EMBED TAB ─────────────────────────────────── */}
          {tab === 'embed' && (
            <>
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: 12, marginBottom: 20, fontSize: 13, color: '#166534' }}>
                💡 Add your mentor card to your <strong>portfolio, blog, or LinkedIn profile</strong> — visitors can book directly from there!
              </div>

              {/* Options */}
              <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Theme</div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {['light', 'dark'].map(t => (
                      <button key={t} onClick={() => setEmbedTheme(t)} style={{
                        flex: 1, padding: '8px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                        background: embedTheme === t ? '#1a1a2e' : '#f3f4f6',
                        color: embedTheme === t ? '#fff' : '#374151',
                        border: embedTheme === t ? '1.5px solid #1a1a2e' : '1.5px solid #e5e7eb',
                      }}>{t === 'light' ? '☀️ Light' : '🌙 Dark'}</button>
                    ))}
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Size</div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {['small', 'medium', 'large'].map(s => (
                      <button key={s} onClick={() => setEmbedSize(s)} style={{
                        flex: 1, padding: '8px', borderRadius: 8, cursor: 'pointer', fontSize: 11, fontWeight: 600,
                        background: embedSize === s ? '#1a1a2e' : '#f3f4f6',
                        color: embedSize === s ? '#fff' : '#374151',
                        border: embedSize === s ? '1.5px solid #1a1a2e' : '1.5px solid #e5e7eb',
                      }}>{s.charAt(0).toUpperCase() + s.slice(1)}</button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Preview */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 8 }}>👁️ Preview</div>
                <div style={{
                  background: embedTheme === 'dark' ? '#1a1a2e' : '#f9fafb',
                  border: '1.5px solid #e5e7eb', borderRadius: 12, padding: 16,
                  display: 'flex', alignItems: 'center', gap: 12,
                  width: embedSize === 'small' ? 280 : embedSize === 'large' ? '100%' : 340,
                }}>
                  <div style={{ width: 52, height: 52, borderRadius: '50%', background: '#' + (mentor?.avatarBg || '1DB954'), display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#fff', fontSize: 18, flexShrink: 0 }}>
                    {mentor?.initials || 'AK'}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: embedTheme === 'dark' ? '#fff' : '#1a1a2e', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{mentor?.name || 'Mentor Name'}</div>
                    <div style={{ fontSize: 12, color: embedTheme === 'dark' ? '#9ca3af' : '#6b7280', marginTop: 2 }}>{mentor?.role || 'Role'} · {mentor?.company || 'Company'}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                      <span style={{ fontSize: 11, color: '#f59e0b' }}>{'★'.repeat(5)}</span>
                      <span style={{ fontSize: 11, color: embedTheme === 'dark' ? '#9ca3af' : '#6b7280' }}>{mentor?.rating || '4.9'} · {mentor?.reviewCount || '0'} reviews</span>
                    </div>
                  </div>
                  {embedSize !== 'small' && (
                    <div style={{ background: '#1DB954', color: '#fff', padding: '6px 12px', borderRadius: 8, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0 }}>Book</div>
                  )}
                </div>
                <div style={{ marginTop: 6 }}>
                  <a href="#!" style={{ color: '#1DB954', fontSize: 12, fontWeight: 600, textDecoration: 'none' }}>🧠 Book a session on DevBrains →</a>
                </div>
              </div>

              {/* Code */}
              <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 8 }}>📋 Embed Code</div>
              <div style={{ position: 'relative' }}>
                <pre style={{
                  background: '#1a1a2e', color: '#a5f3fc', padding: 14, borderRadius: 10,
                  fontSize: 11, lineHeight: 1.6, overflow: 'auto', maxHeight: 160,
                  margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                }}>{embedCode}</pre>
                <button onClick={() => copy(embedCode, 'embed')} style={{
                  position: 'absolute', top: 8, right: 8,
                  padding: '4px 10px', background: copied === 'embed' ? '#1DB954' : 'rgba(255,255,255,0.15)',
                  color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 600,
                }}>{copied === 'embed' ? '✅ Copied!' : '📋 Copy'}</button>
              </div>

              {/* Where to use */}
              <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {[
                  { icon: '🌐', label: 'Portfolio website' },
                  { icon: '📝', label: 'Blog / Medium' },
                  { icon: '💼', label: 'LinkedIn About' },
                  { icon: '📄', label: 'GitHub README' },
                  { icon: '🎨', label: 'Notion page' },
                  { icon: '📱', label: 'Link-in-bio' },
                ].map(({ icon, label }) => (
                  <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#6b7280' }}>
                    <span>{icon}</span><span>{label}</span>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ── REFERRAL TAB ──────────────────────────────── */}
          {tab === 'referral' && (
            <>
              {/* Rewards banner */}
              <div style={{ background: 'linear-gradient(135deg,#1a1a2e,#16213e)', borderRadius: 14, padding: 20, marginBottom: 20, color: '#fff', textAlign: 'center' }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>🎁</div>
                <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>Earn Free Sessions!</div>
                <div style={{ fontSize: 13, color: '#9ca3af', lineHeight: 1.6 }}>
                  Share your referral link. When someone joins and books their first session, <strong style={{ color: '#1DB954' }}>you both get ₹500 credit</strong>.
                </div>
              </div>

              {/* Referral link */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>🔗 Your Referral Link</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input readOnly value={shareUrl} style={{
                    flex: 1, padding: '10px 12px', borderRadius: 8, border: '1.5px solid #e5e7eb',
                    fontSize: 12, color: '#374151', background: '#f9fafb', outline: 'none',
                  }} onClick={e => e.target.select()} />
                  <button onClick={() => copy(shareUrl, 'ref')} style={{
                    padding: '10px 16px', background: copied === 'ref' ? '#1DB954' : '#1a1a2e',
                    color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer',
                    fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap',
                  }}>{copied === 'ref' ? '✅ Copied!' : '📋 Copy'}</button>
                </div>
                <div style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}>
                  Your referral code: <strong style={{ color: '#1DB954', fontFamily: 'monospace', letterSpacing: 1 }}>{referralCode}</strong>
                </div>
              </div>

              {/* Share referral */}
              <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 12 }}>📲 Share your referral</div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20 }}>
                <SocialBtn icon="💬" label="WhatsApp"  color="#25D366" onClick={() => openUrl(`https://wa.me/?text=${encodeURIComponent(`🎁 Join me on DevBrains and we both get ₹500 credit!\n\nUse my referral link: ${shareUrl}`)}`)} />
                <SocialBtn icon="🐦" label="Twitter"   color="#1DA1F2" onClick={() => openUrl(`https://twitter.com/intent/tweet?text=${encodeURIComponent(`Just joined @DevBrains_in for 1:1 tech mentorship! 🧠\n\nJoin with my referral link and we both get ₹500 credit 🎁\n${shareUrl}`)}`)} />
                <SocialBtn icon="📧" label="Email"     color="#6366f1" onClick={() => openUrl(`mailto:?subject=${encodeURIComponent('Join DevBrains — We both get ₹500 credit!')}&body=${encodeURIComponent(`Hi,\n\nI've been using DevBrains for 1:1 tech mentorship and it's been amazing!\n\nJoin using my referral link and we both get ₹500 session credit:\n${shareUrl}\n\nSee you there!`)}`)} />
              </div>

              {/* How it works */}
              <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 12 }}>📋 How it works</div>
              {[
                { step: '1', text: 'Share your unique referral link with friends' },
                { step: '2', text: 'They sign up using your link' },
                { step: '3', text: 'They book their first session' },
                { step: '4', text: 'You both get ₹500 credit instantly! 🎉' },
              ].map(({ step, text }) => (
                <div key={step} style={{ display: 'flex', gap: 12, marginBottom: 10, alignItems: 'flex-start' }}>
                  <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#1DB954', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 12, flexShrink: 0 }}>{step}</div>
                  <div style={{ fontSize: 13, color: '#374151', paddingTop: 4 }}>{text}</div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
